import { DocumentTemplate } from 'types/inventory';
import { Tag } from 'types/clientV2/tag';
import { GenericResource, Lite, POSTSchema } from 'types/api';
import { CustomPropertiesType } from 'types';

export enum LoginRequired {
  REQUIRED = 'required',
  NOT_REQUIRED = 'not_required',
  RECOMMENDED = 'recommended',
}

type CommonSupplierCore = {
  id: string;
  objectId: string;
  name: string;
  altNames: string[];
  active: boolean;
  website: string | null;
  contactName: string | null;
  phone: string | null;
  orderEmail: string | null;
  quotingEmail: string | null;
  paymentEmail: string | null;
  customReelPrice: number | null;
  additionalFeeFlat: number | null;
  additionalFeePercentage: number | null;
  minimumLineSize: number | null;
  minimumOrderSize: number | null;
  minimumOrderForScheduledRelease: number | null;
  minimumLineForScheduledRelease: number | null;
  shippingOptions:
    | {
        price: number;
        shippingLead: number;
      }[]
    | null;
  freeShip: {
    threshold: number | null;
    shippingLead: number | null;
  } | null;
  shippingPriceIsEstimate: boolean;
  allowDropship: boolean;
  authenticity: number | null;
  accuracy: number | null;
  certifications: string[] | null;
  shipsFromCountries: string[] | null;
  bestCaseLead: number | null;
  scheduledReleasePeriod: number | null;
  convenienceReturnWindow: number | null;
  uncertainLead: boolean;
  shipsInLead: number | null;
  buyable: boolean;
  overlap: boolean;
  logo: string | null;
  autoScrape: boolean;
  rescrapeInterval: number | null;
  manufacturerMatchRegex: string | null;
  mpnMatchRegex: string | null;
  productPatchUrl: string | null;
  nonBuyableMfrs: string[] | null;
  supplierTariffAdjustment: number | null;
  buyMarkupPercentDiscount: number | null;
  loginRequired: LoginRequired | null;
};

export type PublicSupplier = CommonSupplierCore & {
  supportsSupplierNetwork: boolean;
  supportsCofactrPay: boolean;
  cofactrPayConfigured: boolean;
  supportsPricingApi: boolean;
  supportsOrderSubmissionApi: boolean;
  supportsOrderStatusApi: boolean;
  supportsOrderPo: boolean;
  supportsOnlineCheckout: boolean;
  supportsNonPartPurchaseLines: boolean;
};

export enum InvoiceApprovalRules {
  DEFAULT = 'default',
  NEVER = 'never',
  ALWAYS = 'always',
  TWO_WAY_MATCH = 'two_way_match',
  THREE_WAY_MATCH = 'three_way_match',
}

export enum OrgSupplierConnection {
  SUPPLIER_NETWORK = 'supplier_network',
  COFACTR_PAY = 'cofactr_pay',
  DIRECT = 'direct',
}

export enum DefaultTrueFalse {
  DEFAULT = 'default',
  TRUE = 'true',
  FALSE = 'false',
}

export enum UpdatePurchaseLinePricesFromBill {
  DEFAULT = 'default',
  TRUE = 'true',
  FALSE = 'false',
  ONLY_LOWER = 'only_lower',
}

export const OrgSupplierConnectionLabels = {
  [OrgSupplierConnection.SUPPLIER_NETWORK]: 'Managed by Cofactr',
  [OrgSupplierConnection.COFACTR_PAY]: 'Cofactr Pay',
  [OrgSupplierConnection.DIRECT]: 'Direct',
};

export type OrgSupplier = CommonSupplierCore & {
  connectionType: OrgSupplierConnection;
  usePricingApi: boolean;
  useOrderSubmissionApi: boolean;
  useOrderStatusApi: boolean;
  useOrderPo: boolean;
  useOnlineCheckout: boolean;
  useCustomReeling: boolean;
  allowNonPartPurchaseLines: boolean;
  automaticallyGeneratePurchases: boolean;
  automaticallyAddRequestsToOpenPurchases: boolean;
  automaticallyApprovePurchases: boolean;
  automaticallySendPurchases: boolean;
  invoiceApproval: InvoiceApprovalRules;
  publicSupplier: PublicSupplier | null;
  externalMetadata: object | null;
  internalMetadata: object | null;
  customProperties: CustomPropertiesType;
  poFooterNotes: string | null;
  defaultPoTemplate: DocumentTemplate['id'] | null;
  tags: Tag[];
  updatePurchaseLinePricesFromBill: UpdatePurchaseLinePricesFromBill;
  ignoreShippingInBillApproval: DefaultTrueFalse;
  updatePurchaseShippingFromBill: DefaultTrueFalse;
  ignoreDutiesInBillApproval: DefaultTrueFalse;
  updatePurchaseDutiesFromBill: DefaultTrueFalse;
  ignoreTaxesInBillApproval: DefaultTrueFalse;
  updatePurchaseTaxesFromBill: DefaultTrueFalse;
  onlyUseTotalForBillApproval: DefaultTrueFalse;
  punchoutEnabled: boolean;
  showPunchoutInDashboard: boolean;
  org: string;
};

export type OrgSupplierLite = Lite<OrgSupplier>;

export type UpdateOrgSupplier = {
  name?: string;
  altNames?: string[];
  active?: boolean;
  website?: string | null;
  contactName?: string | null;
  phone?: string | null;
  orderEmail?: string | null;
  quotingEmail?: string | null;
  paymentEmail?: string | null;
  customReelPrice?: number | null;
  additionalFeeFlat?: number | null;
  additionalFeePercentage?: number | null;
  minimumLineSize?: number | null;
  minimumOrderSize?: number | null;
  minimumOrderForScheduledRelease?: number | null;
  minimumLineForScheduledRelease?: number | null;
  shippingOptions?:
    | {
        price: number;
        shippingLead: number;
      }[]
    | null;
  freeShip?: {
    threshold: number | null;
    shippingLead: number | null;
  } | null;
  shippingPriceIsEstimate?: boolean;
  allowDropship?: boolean;
  certifications?: string[] | null;
  shipsFromCountries?: string[] | null;
  bestCaseLead?: number | null;
  scheduledReleasePeriod?: number | null;
  convenienceReturnWindow?: number | null;
  uncertainLead?: boolean;
  shipsInLead?: number | null;
  buyable?: boolean;
  overlap?: boolean;
  externalMetadata?: object | null;
  internalMetadata?: object | null;
  customProperties?: object;
  connectionType?: OrgSupplierConnection;
  usePricingApi?: boolean;
  useOrderSubmissionApi?: boolean;
  useOrderStatusApi?: boolean;
  useOrderPo?: boolean;
  useOnlineCheckout?: boolean;
  useCustomReeling?: boolean;
  allowNonPartPurchaseLines?: boolean;
  automaticallyGeneratePurchases?: boolean;
  automaticallyAddRequestsToOpenPurchases?: boolean;
  automaticallyApprovePurchases?: boolean;
  automaticallySendPurchases?: boolean;
  invoiceApproval?: InvoiceApprovalRules;
  defaultPoTemplate?: DocumentTemplate['id'] | null;
  poFooterNotes?: string | null;
  updatePurchaseLinePricesFromBill?: UpdatePurchaseLinePricesFromBill;
  ignoreShippingInBillApproval?: DefaultTrueFalse;
  updatePurchaseShippingFromBill?: DefaultTrueFalse;
  ignoreDutiesInBillApproval?: DefaultTrueFalse;
  updatePurchaseDutiesFromBill?: DefaultTrueFalse;
  ignoreTaxesInBillApproval?: DefaultTrueFalse;
  updatePurchaseTaxesFromBill?: DefaultTrueFalse;
  onlyUseTotalForBillApproval?: DefaultTrueFalse;
  tags?: Tag['id'][];
  autoScrape?: boolean;
  rescrapeInterval?: number | null;
  manufacturerMatchRegex?: string | null;
  mpnMatchRegex?: string | null;
  productPatchUrl?: string | null;
};

export type CreateOrgSupplier = UpdateOrgSupplier & {
  alsoCreatePublicSupplier: boolean;
  publicSupplier: PublicSupplier['id'] | null;
};

export const URL_OR_DOMAIN_REGEX =
  /^(((http|https):\/\/|)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,8}(:[0-9]{1,5})?(\/.*)?)$/i;

export type OrgSupplierCredential = GenericResource & {
  name: string;
  url: string;
  username: string;
  password: string;
  proxyTargetIp: string;
  orgSupplier: OrgSupplier['id'];
};

export type PutOrgSupplierCredential = POSTSchema<
  OrgSupplierCredential,
  'username' | 'password',
  'proxyTargetIp'
>;
